/**
 * Set what a role may reach, one step at a time.
 *
 * A single grid of every menu against every verb is 26 rows of tick boxes and
 * nobody reads it. The same decision taken in order — who, then which parts of
 * the app, then what they may do in each, then a look at the result — is the
 * same information with somewhere to stand at each point.
 *
 * The tree and the verbs come from the server, so the wizard can never offer a
 * Delete on a screen that has no delete. Saving writes the org's policy, which
 * the API route guards read on the next request.
 */
import { useState, useMemo } from 'react';
import Icon from '../../lib/icons.jsx';

const STEPS = ['Role', 'Modules', 'Permissions', 'Review'];

/** Reads better than "create, edit, delete" repeated on every row. */
const VERB_LABEL = { view: 'View', create: 'Create', edit: 'Edit', delete: 'Delete' };

/** A menu is "on" when it can at least be opened. */
const isOn = (policy, key) => (policy[key] || []).includes('view');

export default function AccessWizard({ tree, roles, onClose, onSave, startRole, startStep = 0 }) {
  const [step, setStep] = useState(startStep);
  const [role, setRole] = useState(startRole || 'Manager');
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(roles)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const policy = draft[role] || {};
  const modules = tree.modules;
  const roleNames = tree.names || Object.keys(tree.defaults);
  const builtIn = tree.builtIn || Object.keys(tree.defaults);

  /* A module is included when any of its menus can be opened. */
  const chosenModules = useMemo(
    () => modules.filter(m => m.menus.some(x => isOn(policy, x.key))).map(m => m.key),
    [modules, policy]
  );

  const write = fn => setDraft(d => {
    const next = JSON.parse(JSON.stringify(d));
    next[role] = next[role] || {};
    fn(next[role]);
    return next;
  });

  /* Turning a module on opens its menus for reading; the verbs are step three.
     Turning it off takes the whole module away, which is what "not for them"
     means. */
  const toggleModule = mod => write(p => {
    const on = mod.menus.some(x => isOn(p, x.key));
    for (const menu of mod.menus) {
      if (on) delete p[menu.key];
      else p[menu.key] = ['view'];
    }
  });

  const toggleMenu = menu => write(p => {
    if (isOn(p, menu.key)) delete p[menu.key];
    else p[menu.key] = ['view'];
  });

  const toggleVerb = (menu, verb) => write(p => {
    const held = new Set(p[menu.key] || []);
    if (verb === 'view') return; /* handled by toggleMenu */
    if (held.has(verb)) held.delete(verb); else held.add(verb);
    p[menu.key] = menu.verbs.filter(v => held.has(v) || v === 'view');
  });

  const setAll = (mod, on) => write(p => {
    for (const menu of mod.menus) {
      if (on) p[menu.key] = [...menu.verbs];
      else if (isOn(p, menu.key)) p[menu.key] = ['view'];
    }
  });

  const reset = () => setDraft(d => ({ ...d, [role]: JSON.parse(JSON.stringify(tree.defaults[role] || {})) }));

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      await onSave(draft);
      onClose();
    } catch (e) {
      setError(e?.message || 'Could not save');
      setBusy(false);
    }
  };

  const openCount = Object.keys(policy).filter(k => isOn(policy, k)).length;
  const last = step === STEPS.length - 1;

  return (
    <div className="veil open" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sheet wide awiz" role="dialog" aria-modal="true">
        <div className="shead">
          <div className="ic g-purple"><Icon name="lock" /></div>
          <h3>Permissions</h3>
          <button type="button" className="iconbtn" aria-label="Close" onClick={onClose}>
            <Icon name="x" />
          </button>
        </div>

        <ol className="wsteps">
          {STEPS.map((s, i) => (
            <li key={s} className={i === step ? 'on' : i < step ? 'done' : undefined}>
              <span className="wnum">{i < step ? <Icon name="check" /> : i + 1}</span>
              <span className="wlab">{s}</span>
            </li>
          ))}
        </ol>

        <div className="wbody">
          {step === 0 ? (
            <>
              <p className="whint">Which role are you setting up? Everyone with that role gets the same access.</p>
              <div className="wcards">
                {roleNames.map(r => (
                  <button
                    type="button"
                    key={r}
                    className={`wcard${role === r ? ' on' : ''}${r === 'Owner' ? ' locked' : ''}`}
                    disabled={r === 'Owner'}
                    onClick={() => setRole(r)}
                  >
                    <b>{r}</b>
                    <small>
                      {r === 'Owner'
                        ? 'Always has everything'
                        : `${Object.keys(draft[r] || {}).filter(k => isOn(draft[r], k)).length} menus`}
                      {builtIn.includes(r) ? '' : ' · added by you'}
                    </small>
                  </button>
                ))}
              </div>
              <p className="whint">
                The Owner is left alone on purpose — it is the account that cannot lock itself
                out, or nobody would be able to give the permissions back.
              </p>
            </>
          ) : null}

          {step === 1 ? (
            <>
              <p className="whint">
                Which parts of the app should <b>{role}</b> reach? You will choose what they can
                do inside each one next.
              </p>
              <div className="wcards">
                {modules.map(m => {
                  const on = chosenModules.includes(m.key);
                  return (
                    <button
                      type="button"
                      key={m.key}
                      className={`wcard${on ? ' on' : ''}`}
                      onClick={() => toggleModule(m)}
                    >
                      <span className="wtick">{on ? <Icon name="check" /> : null}</span>
                      <b>{m.name}</b>
                      <small>
                        {on
                          ? `${m.menus.filter(x => isOn(policy, x.key)).length} of ${m.menus.length} menus`
                          : `${m.menus.length} menus`}
                      </small>
                    </button>
                  );
                })}
              </div>
            </>
          ) : null}

          {step === 2 ? (
            <>
              <p className="whint">
                What can <b>{role}</b> do in each menu? A menu they cannot view is not offered at
                all, so its other options come away with it.
              </p>
              {modules.filter(m => chosenModules.includes(m.key)).map(m => (
                <div className="wmod" key={m.key}>
                  <div className="wmodhead">
                    <b>{m.name}</b>
                    <div className="row">
                      <button type="button" className="btn sm" onClick={() => setAll(m, true)}>
                        Everything
                      </button>
                      <button type="button" className="btn sm" onClick={() => setAll(m, false)}>
                        View only
                      </button>
                    </div>
                  </div>

                  <table className="wtable">
                    <thead>
                      <tr>
                        <th>Menu</th>
                        {tree.verbs.map(v => <th key={v}>{VERB_LABEL[v]}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {m.menus.map(menu => {
                        const on = isOn(policy, menu.key);
                        return (
                          <tr key={menu.key} className={on ? undefined : 'off'}>
                            <td><b>{menu.name}</b></td>
                            {tree.verbs.map(v => {
                              const supported = menu.verbs.includes(v);
                              if (!supported) {
                                return (
                                  <td key={v}>
                                    <span className="wna" title="This screen has nothing of the kind">—</span>
                                  </td>
                                );
                              }
                              return (
                                <td key={v}>
                                  <input
                                    type="checkbox"
                                    className="chk"
                                    checked={(policy[menu.key] || []).includes(v)}
                                    disabled={v !== 'view' && !on}
                                    onChange={() => (v === 'view' ? toggleMenu(menu) : toggleVerb(menu, v))}
                                  />
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}

              {!chosenModules.length ? (
                <p className="whint">No modules chosen — go back a step and pick at least one.</p>
              ) : null}
            </>
          ) : null}

          {step === 3 ? (
            <>
              <p className="whint">
                <b>{role}</b> will be able to open <b>{openCount}</b>{' '}
                {openCount === 1 ? 'menu' : 'menus'}. Anything not listed is hidden from them, and
                refused by the API if they ask for it anyway.
              </p>
              {modules.map(m => {
                const on = m.menus.filter(x => isOn(policy, x.key));
                if (!on.length) return null;
                return (
                  <div className="wsum" key={m.key}>
                    <b>{m.name}</b>
                    <div className="wsumrows">
                      {on.map(menu => {
                        const held = (policy[menu.key] || []).filter(v => v !== 'view');
                        return (
                          <div className="wsumrow" key={menu.key}>
                            <span>{menu.name}</span>
                            <span className="wsumverbs">
                              {held.length
                                ? held.map(v => VERB_LABEL[v]).join(' · ')
                                : <i>view only</i>}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {!openCount ? (
                <p className="whint">
                  Nothing is open to this role. They will be able to sign in and see an empty
                  workspace — which is a fair choice, but worth meaning.
                </p>
              ) : null}
            </>
          ) : null}

          {error ? <div className="err">{error}</div> : null}
        </div>

        <div className="sfoot">
          {builtIn.includes(role) ? (
            <button type="button" className="btn" onClick={reset}>
              <Icon name="refresh" />Reset {role} to default
            </button>
          ) : <span />}
          <div className="spacer" style={{ flex: 1 }} />
          {step > 0 ? (
            <button type="button" className="btn" onClick={() => setStep(s => s - 1)}>Back</button>
          ) : null}
          {last ? (
            <button type="button" className="btn primary" disabled={busy} onClick={save}>
              {busy ? 'Saving…' : 'Save permissions'}
            </button>
          ) : (
            <button type="button" className="btn primary" onClick={() => setStep(s => s + 1)}>
              Continue<Icon name="chev" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
